'use client';

import { useRef, useState, useEffect, type ReactNode } from 'react';

interface LazySectionProps {
  children: ReactNode;
  /** Minimum height for the skeleton placeholder */
  minHeight?: string;
  /** Root margin for triggering load before section is visible */
  rootMargin?: string;
}

function SectionSkeleton({ minHeight }: { minHeight: string }) {
  return (
    <div className="animate-pulse space-y-4 px-4 py-12" style={{ minHeight }}>
      <div className="mx-auto h-8 w-2/3 rounded-md bg-gray-200" />
      <div className="mx-auto h-4 w-1/2 rounded-md bg-gray-200" />
      <div className="mx-auto mt-8 grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-48 rounded-lg bg-gray-200" />
        ))}
      </div>
    </div>
  );
}

export function LazySection({
  children,
  minHeight = '300px',
  rootMargin = '200px',
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Parse the rootMargin's leading value (e.g. "200px") into a pixel number so
    // the synchronous fallback below uses the same trigger distance as the observer.
    const marginPx = Number.parseInt(rootMargin, 10) || 0;

    let settled = false;

    // Measures whether the section is within (or past) the viewport, using the same
    // trigger distance as the observer's rootMargin.
    const isAlreadyInView = () => {
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top <= viewportHeight + marginPx && rect.bottom >= -marginPx;
    };

    let frames = 0;
    let rafId = 0;

    const checkInView = () => {
      if (isAlreadyInView()) reveal();
    };

    const cleanupFallbacks = () => {
      window.removeEventListener('scroll', checkInView);
      window.removeEventListener('resize', checkInView);
      cancelAnimationFrame(rafId);
    };

    const reveal = () => {
      if (settled) return;
      settled = true;
      setIsVisible(true);
      observer.disconnect();
      cleanupFallbacks();
    };

    // The IntersectionObserver is the primary trigger. On reload, though, the
    // browser restores scroll *after* mount, and when it restores to the very
    // bottom of the page the observer can miss its initial notification — leaving
    // this section stuck on its skeleton. The fallbacks below re-check on scroll
    // restoration, resize, and a few frames so we always catch that case.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) reveal();
      },
      { rootMargin }
    );
    observer.observe(el);

    // Synchronous check for the case where scroll is already restored by mount.
    if (isAlreadyInView()) {
      reveal();
      return () => {
        observer.disconnect();
        cleanupFallbacks();
      };
    }

    // Scroll restoration on reload fires a scroll event; resize covers late layout.
    window.addEventListener('scroll', checkInView, { passive: true });
    window.addEventListener('resize', checkInView, { passive: true });

    // Poll a handful of frames after mount. Browsers restore scroll position over
    // one or more frames following the first paint, so a single rAF is not enough.
    const pollFrames = () => {
      if (settled) return;
      checkInView();
      if (settled) return;
      frames += 1;
      if (frames < 20) {
        rafId = requestAnimationFrame(pollFrames);
      }
    };
    rafId = requestAnimationFrame(pollFrames);

    return () => {
      observer.disconnect();
      cleanupFallbacks();
    };
  }, [rootMargin]);

  return <div ref={ref}>{isVisible ? children : <SectionSkeleton minHeight={minHeight} />}</div>;
}
